import { ROLE_MARKER } from '../../theme/typeRoles';

// Labels text in the typography preview frame with the dashboard role that
// controls it, so Marketing can see what each setting changes. Everything here
// runs inside the preview iframe's own document and never touches the live site.
//
// Every role style carries ROLE_MARKER (theme/typeRoles.js). Custom properties
// normally inherit, so the frame registers it as NON-inherited — then only the
// element that actually sets a role reports it, not all of its children.
//
// To stay readable on busy areas (nav bars, footer link lists):
//  - each labeled element gets a thin outline, but runs of neighboring elements
//    with the same role share ONE short badge ("Nav 16 ×6");
//  - badges never overlap — one that would collide is skipped (its element keeps
//    the outline);
//  - hovering any outlined element shows its full name and size.

// One color per role group (the editor's cards), also used for the legend.
export const ROLE_GROUP_COLORS = {
  Headings: '#1565c0',
  Body: '#546e7a',
  Buttons: '#a07a2c',
  Hero: '#7b1fa2',
  Navigation: '#00838f',
  Footer: '#6d4c41',
  Forms: '#2e7d32',
};

// Compact badge names; the full registry label shows on hover.
const SHORT_LABEL = {
  h1: 'H1',
  h2: 'H2',
  h3: 'H3',
  h4: 'H4',
  'h5-h6': 'H5/H6',
  body: 'Body',
  'body-small': 'Small',
  button: 'Button',
  'hero-headline': 'Hero',
  'hero-subheadline': 'Hero sub',
  'hero-cta': 'Hero CTA',
  'nav-main': 'Nav',
  'nav-utility': 'Top bar',
  'footer-heading': 'Footer head',
  'footer-text': 'Footer',
  'footer-legal': 'Legal',
  'form-label': 'Label',
  'form-input': 'Input',
  'form-helper': 'Helper',
};

const LAYER_ID = 'jet-role-labels';
const MAX_ELEMENTS = 400;
const NEIGHBOR_GAP = 12; // px — same-role elements this close share a badge

const hasContent = (el) =>
  el.textContent.trim().length > 0 ||
  el.matches('input,textarea,select') ||
  !!el.querySelector('input:not([type=hidden]),textarea,select');

const gapBetween = (a, b) =>
  Math.max(0, Math.max(a.left, b.left) - Math.min(a.right, b.right), Math.max(a.top, b.top) - Math.min(a.bottom, b.bottom));

const overlaps = (a, b) => a.left < b.right && b.left < a.right && a.top < b.bottom && b.top < a.bottom;

/**
 * Starts labeling inside `frame` (a same-origin iframe that has loaded).
 * `roles`: registry entries ({ key, label, group }). `getScale()`: how much the
 * frame is shrunk on screen, so badges can be counter-scaled to stay readable.
 * `onRoleClick(key)`: optional — makes badges/hover tags clickable. Returns a
 * cleanup function.
 */
export function attachRoleLabels(frame, { roles, getScale = () => 1, onRoleClick }) {
  const w = frame?.contentWindow;
  const d = frame?.contentDocument;
  if (!w || !d?.body) return () => {};

  try {
    w.CSS.registerProperty({ name: ROLE_MARKER, syntax: '*', inherits: false });
  } catch (e) {
    // Already registered for this document (labels toggled off and on again).
  }

  const info = new Map(roles.map((r) => [r.key, r]));
  d.getElementById(LAYER_ID)?.remove();
  const layer = d.createElement('div');
  layer.id = LAYER_ID;
  layer.style.cssText = 'position:fixed;inset:0;pointer-events:none;z-index:2147483647;';
  const marks = d.createElement('div'); // outlines + badges, redrawn on scroll
  const hover = d.createElement('div'); // the hovered element's outline + full tag
  layer.append(marks, hover);
  d.body.appendChild(layer);

  let clusters = []; // [{ role, members: [el] }]
  let drawn = []; // [{ el, role, rect }] — what's on screen, for hover lookup
  let hovered = null;
  let raf = 0;
  let collectTimer = 0;

  const colorOf = (role) => ROLE_GROUP_COLORS[role.group] || '#455a64';
  const sizeOf = (el) => Math.round(parseFloat(w.getComputedStyle(el).fontSize));

  const makeTag = (text, color, title, key) => {
    const s = getScale() || 1;
    const tag = d.createElement('div');
    tag.textContent = text;
    tag.title = title;
    tag.style.cssText =
      `position:fixed;left:0;top:0;font:600 ${10 / s}px/1.3 system-ui,-apple-system,"Segoe UI",sans-serif;` +
      `letter-spacing:0;text-transform:none;color:#fff;background:${color};padding:${1 / s}px ${4 / s}px;` +
      `border-radius:${3 / s}px;white-space:nowrap;box-shadow:0 ${1 / s}px ${2 / s}px rgba(0,0,0,.3);` +
      `pointer-events:${onRoleClick ? 'auto' : 'none'};cursor:${onRoleClick ? 'pointer' : 'default'};`;
    if (onRoleClick) {
      tag.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        onRoleClick(key);
      });
    }
    return tag;
  };

  const outline = (rect, color, solid) => {
    const s = getScale() || 1;
    const box = d.createElement('div');
    const width = (solid ? 2 : 1) / s;
    box.style.cssText =
      `position:fixed;left:${rect.left}px;top:${rect.top}px;width:${rect.width}px;height:${rect.height}px;` +
      `outline:${width}px ${solid ? 'solid' : 'dashed'} ${color};outline-offset:${1 / s}px;` +
      `opacity:${solid ? 1 : 0.75};`;
    return box;
  };

  // Which elements set a role themselves, grouped into runs of neighbors.
  const collect = () => {
    collectTimer = 0;
    const found = [];
    const all = d.body.querySelectorAll('*');
    for (let i = 0; i < all.length && found.length < MAX_ELEMENTS; i += 1) {
      const el = all[i];
      if (el !== layer && !layer.contains(el)) {
        const key = w.getComputedStyle(el).getPropertyValue(ROLE_MARKER).trim();
        if (key && info.has(key) && hasContent(el)) found.push({ el, role: info.get(key) });
      }
    }
    // Document order: a run continues while the role stays the same and the
    // next element shares the parent or sits right next to the previous one.
    clusters = [];
    found.forEach(({ el, role }) => {
      const last = clusters[clusters.length - 1];
      const prev = last?.members[last.members.length - 1];
      const joins =
        last &&
        last.role.key === role.key &&
        (prev.parentElement === el.parentElement ||
          gapBetween(prev.getBoundingClientRect(), el.getBoundingClientRect()) <= NEIGHBOR_GAP);
      if (joins) last.members.push(el);
      else clusters.push({ role, members: [el] });
    });
    schedule();
  };

  const visibleRect = (el, vw, vh) => {
    const r = el.getBoundingClientRect();
    if (r.width < 2 || r.height < 2 || r.bottom < 0 || r.top > vh || r.right < 0 || r.left > vw) return null;
    const cs = w.getComputedStyle(el);
    if (cs.visibility === 'hidden' || cs.opacity === '0') return null;
    return r;
  };

  // Outlines for every labeled element on screen; one badge per run, placed
  // where it doesn't collide with a badge already drawn (else skipped).
  const draw = () => {
    raf = 0;
    marks.textContent = '';
    drawn = [];
    const vw = w.innerWidth;
    const vh = w.innerHeight;
    const placed = [];

    clusters.forEach(({ role, members }) => {
      const color = colorOf(role);
      const rects = [];
      members.forEach((el) => {
        const r = visibleRect(el, vw, vh);
        if (!r) return;
        rects.push(r);
        drawn.push({ el, role, rect: r });
        marks.appendChild(outline(r, color, false));
      });
      if (!rects.length) return;

      const count = members.length > 1 ? ` ×${members.length}` : '';
      const tag = makeTag(
        `${SHORT_LABEL[role.key] || role.label} ${sizeOf(members[0])}${count}`,
        color,
        `${role.label}${onRoleClick ? ' — click to edit' : ''}`,
        role.key
      );
      marks.appendChild(tag);
      const { width: tw, height: th } = tag.getBoundingClientRect();
      const a = rects[0];
      const spots = [
        [a.left, a.top - th], // above, left
        [a.left, a.top], // inside, top-left
        [a.right - tw, a.top - th], // above, right
        [a.left, a.bottom], // below, left
      ];
      const spot = spots
        .map(([left, top]) => ({ left, top, right: left + tw, bottom: top + th }))
        .find((r) => r.top >= 0 && r.left >= 0 && r.right <= vw && r.bottom <= vh && !placed.some((p) => overlaps(p, r)));
      if (!spot) {
        tag.remove(); // no room — the outline stays; hover shows the label
        return;
      }
      tag.style.left = `${spot.left}px`;
      tag.style.top = `${spot.top}px`;
      placed.push(spot);
    });
    renderHover();
  };

  // Full name + size for whatever is under the pointer (innermost match).
  function renderHover() {
    hover.textContent = '';
    if (!hovered) return;
    const item = drawn.find((x) => x.el === hovered.el);
    if (!item) return;
    const { rect, role, el } = item;
    const color = colorOf(role);
    hover.appendChild(outline(rect, color, true));
    const tag = makeTag(`${role.label} · ${sizeOf(el)}px`, color, onRoleClick ? 'Click to edit' : role.label, role.key);
    hover.appendChild(tag);
    const { width: tw, height: th } = tag.getBoundingClientRect();
    const left = Math.min(Math.max(0, rect.left), w.innerWidth - tw);
    const top = rect.top >= th ? rect.top - th : Math.min(rect.bottom, w.innerHeight - th);
    tag.style.left = `${left}px`;
    tag.style.top = `${top}px`;
  }

  const onMove = (e) => {
    if (hover.contains(e.target) || marks.contains(e.target)) return; // over a tag
    const hit = drawn
      .filter(({ rect: r }) => e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom)
      .sort((x, y) => x.rect.width * x.rect.height - y.rect.width * y.rect.height)[0];
    if ((hit?.el || null) === (hovered?.el || null)) return;
    hovered = hit || null;
    renderHover();
  };
  const onLeave = (e) => {
    if (e.relatedTarget) return;
    hovered = null;
    renderHover();
  };

  function schedule() {
    if (!raf) raf = w.requestAnimationFrame(draw);
  }
  const scheduleCollect = () => {
    if (!collectTimer) collectTimer = w.setTimeout(collect, 150);
  };

  // Page content or styles changed (data loading, new preview CSS, navigation).
  const mo = new w.MutationObserver((mutations) => {
    if (mutations.every((m) => m.target === layer || layer.contains(m.target))) return;
    scheduleCollect();
  });
  mo.observe(d.body, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['class', 'style'] });
  mo.observe(d.head, { childList: true, subtree: true, characterData: true });
  w.addEventListener('scroll', schedule, true);
  w.addEventListener('resize', schedule);
  d.addEventListener('mousemove', onMove, true);
  d.addEventListener('mouseout', onLeave, true);
  collect();

  return () => {
    mo.disconnect();
    w.removeEventListener('scroll', schedule, true);
    w.removeEventListener('resize', schedule);
    d.removeEventListener('mousemove', onMove, true);
    d.removeEventListener('mouseout', onLeave, true);
    if (raf) w.cancelAnimationFrame(raf);
    if (collectTimer) w.clearTimeout(collectTimer);
    layer.remove();
  };
}
